/**
 * Countdown timer for the password / coming soon page.
 *
 * Usage:
 *   <countdown-timer data-launch="2026-09-11T12:00:00Z" data-ended-text="...">
 *     <span data-unit="days"></span> ... <p class="pcs__countdown-ended" hidden></p>
 *   </countdown-timer>
 *
 * The launch instant must be an ISO 8601 string with a timezone (e.g. a trailing "Z")
 * so that every visitor counts down to the same moment regardless of their local zone.
 */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    const launch = Date.parse(this.dataset.launch || '');
    if (Number.isNaN(launch)) {
      console.warn('[countdown-timer] Invalid data-launch value:', this.dataset.launch);
      return;
    }

    this.launch = launch;
    this.units = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };
    this.grid = this.querySelector('.pcs__countdown-grid');
    this.endedEl = this.querySelector('.pcs__countdown-ended');

    this.tick();
    this.interval = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.interval);
  }

  tick() {
    const diff = this.launch - Date.now();

    if (diff <= 0) {
      this.finish();
      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    const values = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };

    for (const [unit, el] of Object.entries(this.units)) {
      if (!el) continue;
      const text = String(values[unit]).padStart(2, '0');
      if (el.textContent !== text) el.textContent = text;
    }
  }

  finish() {
    window.clearInterval(this.interval);
    for (const el of Object.values(this.units)) {
      if (el) el.textContent = '00';
    }
    const endedText = this.dataset.endedText;
    if (endedText && this.endedEl) {
      if (this.grid) this.grid.hidden = true;
      this.endedEl.textContent = endedText;
      this.endedEl.hidden = false;
    }
  }
}

/**
 * Shows a pinned "scroll down" cue until the product cards have actually been
 * reached. It reappears if the visitor scrolls back above the products.
 *
 * The cue watches the strip of cards rather than its wrapper, otherwise the
 * heading alone scrolling into view would be enough to dismiss it.
 */
class ScrollCue {
  /**
   * @param {HTMLElement} cue
   * @param {HTMLElement} target - the element that must be visible to dismiss the cue
   */
  constructor(cue, target) {
    this.cue = cue;
    this.target = target;

    this.observer = new IntersectionObserver((entries) => this.update(entries), {
      // Require a decent slice of the cards to be on screen, and ignore the
      // bottom of the viewport that the cue itself covers.
      threshold: [0, 0.35, 0.7],
      rootMargin: '0px 0px -96px 0px',
    });

    this.observer.observe(target);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  update(entries) {
    const entry = entries[0];
    if (!entry) return;

    const reached = entry.isIntersecting && entry.intersectionRatio >= 0.35;
    this.cue.classList.toggle('is-hidden', reached);
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}

for (const cue of document.querySelectorAll('[data-scroll-cue]')) {
  const carousel = document.querySelector('[data-product-carousel]');
  // The strip is clipped and stationary; the cards inside it translate sideways,
  // so the strip is the reliable thing to observe.
  const target = carousel?.querySelector('.pcs__marquee') ?? carousel;

  if (target) new ScrollCue(/** @type {HTMLElement} */ (cue), /** @type {HTMLElement} */ (target));
}
